import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/layout/Layout';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEmpresa } from '@/contexts/EmpresaContext';
import { useImageUpload } from '@/hooks/useImageUpload';
import GrupoEmpresaSection from './components/GrupoEmpresaSection';

const LOGO_STORAGE_KEY = 'empresa_logo_url';

export default function EmpresaPage() {
  const { isAuthenticated } = useAdminAuth();
  const { empresaSelecionada } = useEmpresa();

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(() => {
    return localStorage.getItem(LOGO_STORAGE_KEY) || null;
  });

  const {
    previewUrl: logoPreview,
    uploadedUrl: newLogoUrl,
    isUploading: isLogoUploading,
    error: logoError,
    fileInputRef: logoFileRef,
    handleFileSelect: handleLogoSelect,
    triggerFileInput: triggerLogoInput,
    clearImage: clearLogo,
    resetError: resetLogoError,
  } = useImageUpload({ bucket: 'company-logos', prefix: `empresa-${empresaSelecionada.id}-`, maxSizeMB: 3 });

  const hiddenLogoInputRef = useRef<HTMLInputElement | null>(null);

  // Atualiza logo quando upload completa
  useEffect(() => {
    if (newLogoUrl) {
      setLogoUrl(newLogoUrl);
      localStorage.setItem(LOGO_STORAGE_KEY, newLogoUrl);
    }
  }, [newLogoUrl]);

  const [razaoSocial, setRazaoSocial] = useState('getPoints Incentivos LTDA');
  const [nomeFantasia, setNomeFantasia] = useState(empresaSelecionada.nome);
  const [cnpj] = useState(empresaSelecionada.cnpj);
  const [inscricaoEstadual] = useState('123.456.789.012');
  const [inscricaoMunicipal] = useState('987654321');
  const [telefone, setTelefone] = useState('(11) 3456-7890');
  const [email, setEmail] = useState('contato@getpoints.com.br');
  const [cep, setCep] = useState('04538-132');
  const [endereco, setEndereco] = useState('Av. Brigadeiro Faria Lima');
  const [numero, setNumero] = useState('1471');
  const [complemento, setComplemento] = useState('10º andar, sala 1001');
  const [bairro, setBairro] = useState('Jardim Paulistano');
  const [cidade, setCidade] = useState('São Paulo');
  const [estado, setEstado] = useState('SP');
  const [pais, setPais] = useState('Brasil');
  const [ramo, setRamo] = useState('Tecnologia / Incentivos');
  const [segmento, setSegmento] = useState('SaaS B2B');
  const [tamanho, setTamanho] = useState('500-1000 colaboradores');
  const [faturamento, setFaturamento] = useState('R$ 50M - 100M/ano');
  const [site, setSite] = useState('www.getpoints.com.br');

  const mostrarToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSalvar = () => {
    if (!razaoSocial.trim()) {
      mostrarToast('Razão social é obrigatória', 'error');
      return;
    }
    if (!nomeFantasia.trim()) {
      mostrarToast('Nome fantasia é obrigatório', 'error');
      return;
    }
    mostrarToast('Dados da empresa atualizados com sucesso', 'success');
    setEditMode(false);
  };

  const handleRemoverLogo = () => {
    setLogoUrl(null);
    localStorage.removeItem(LOGO_STORAGE_KEY);
    clearLogo();
    mostrarToast('Logo removida', 'success');
  };

  // Campos que podem ser editados
  const inputEditable = (value: string, onChange: (v: string) => void, placeholder?: string) => (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={!editMode}
      className={`w-full px-3 py-2 border rounded-lg text-sm text-gray-800 transition-all ${
        editMode
          ? 'border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500'
          : 'border-gray-100 bg-gray-50 text-gray-500'
      }`}
    />
  );

  // Campos imutáveis (CNPJ, Inscrição Estadual, Inscrição Municipal)
  const inputImutavel = (value: string) => (
    <input
      type="text"
      value={value}
      disabled
      className="w-full px-3 py-2 border border-gray-100 bg-gray-50 rounded-lg text-sm text-gray-400 cursor-not-allowed"
      title="Este campo não pode ser alterado"
    />
  );

  if (!isAuthenticated) return null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dados da Empresa</h1>
            <p className="text-sm text-gray-500 mt-1">
              Informações cadastrais e fiscais da empresa
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setEditMode(!editMode)}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={editMode ? 'ri-close-line' : 'ri-edit-line'}></i>
              </div>
              <span>{editMode ? 'Cancelar' : 'Editar'}</span>
            </button>
            {editMode && (
              <button
                onClick={handleSalvar}
                className="px-4 py-1.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold text-xs rounded-lg transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                Salvar
              </button>
            )}
          </div>
        </div>

        {/* Header card */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-6 text-white flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Logo com upload */}
            <div className="relative group">
              {logoPreview || logoUrl ? (
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 relative">
                  <img
                    src={logoPreview || logoUrl || ''}
                    alt={`Logo ${nomeFantasia}`}
                    className="w-full h-full object-cover"
                  />
                  {isLogoUploading && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur cursor-pointer hover:bg-white/30 transition-all"
                  onClick={triggerLogoInput}
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-building-4-line text-white text-xl"></i>
                  </div>
                </div>
              )}

              {/* Botão de upload overlay */}
              <button
                onClick={triggerLogoInput}
                className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-orange-50 transition-all cursor-pointer"
                title="Alterar logo"
              >
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-camera-line text-gray-500 text-[10px]"></i>
                </div>
              </button>

              <input
                ref={(el) => {
                  hiddenLogoInputRef.current = el;
                  if (logoFileRef) {
                    (logoFileRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                  }
                }}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleLogoSelect(file);
                }}
                className="hidden"
              />
            </div>

            <div>
              <h2 className="text-xl font-bold">{nomeFantasia}</h2>
              <p className="text-sm text-blue-100 mt-0.5">{razaoSocial}</p>
              <p className="text-xs text-blue-200 mt-1">CNPJ: {cnpj}</p>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-xs text-blue-200">Status do cadastro</p>
            <span className="inline-flex items-center text-xs font-bold bg-white/20 text-white px-3 py-1 rounded-full mt-1 backdrop-blur">
              <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-1.5"></div>
              Ativo
            </span>
          </div>
        </div>

        {/* Erro de upload e botão remover */}
        {(logoError || (logoPreview || logoUrl)) && (
          <div className="flex items-center space-x-3">
            {logoError && (
              <p className="text-xs text-red-500">{logoError}</p>
            )}
            {(logoPreview || logoUrl) && !isLogoUploading && (
              <button
                onClick={handleRemoverLogo}
                className="text-xs text-red-500 hover:text-red-600 font-medium cursor-pointer transition-colors"
              >
                Remover logo
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Dados Fiscais */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-4">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-text-line text-orange-500"></i>
              </div>
              <span>Dados Fiscais</span>
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Razão Social</label>
                {inputEditable(razaoSocial, setRazaoSocial)}
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Nome Fantasia</label>
                {inputEditable(nomeFantasia, setNomeFantasia)}
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  CNPJ
                  <span className="ml-1 text-[10px] font-normal text-gray-400">(não editável)</span>
                </label>
                {inputImutavel(cnpj)}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Inscrição Estadual
                    <span className="ml-1 text-[10px] font-normal text-gray-400">(não editável)</span>
                  </label>
                  {inputImutavel(inscricaoEstadual)}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Inscrição Municipal
                    <span className="ml-1 text-[10px] font-normal text-gray-400">(não editável)</span>
                  </label>
                  {inputImutavel(inscricaoMunicipal)}
                </div>
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-4">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line text-orange-500"></i>
              </div>
              <span>Endereço</span>
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">CEP</label>
                {inputEditable(cep, setCep)}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Logradouro</label>
                  {inputEditable(endereco, setEndereco)}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Nº</label>
                  {inputEditable(numero, setNumero)}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Complemento</label>
                {inputEditable(complemento, setComplemento)}
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Bairro</label>
                {inputEditable(bairro, setBairro)}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Cidade</label>
                  {inputEditable(cidade, setCidade)}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Estado</label>
                  {inputEditable(estado, setEstado)}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">País</label>
                {inputEditable(pais, setPais)}
              </div>
            </div>
          </div>

          {/* Contato e Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-4">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-orange-500"></i>
                </div>
                <span>Contato</span>
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Telefone</label>
                  {inputEditable(telefone, setTelefone)}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Email corporativo</label>
                  {inputEditable(email, setEmail)}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Website</label>
                  {inputEditable(site, setSite)}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-900 flex items-center space-x-2 mb-4">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bar-chart-line text-orange-500"></i>
                </div>
                <span>Perfil da Empresa</span>
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Ramo de atividade</label>
                  {inputEditable(ramo, setRamo)}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Segmento</label>
                  {inputEditable(segmento, setSegmento)}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Porte</label>
                  {inputEditable(tamanho, setTamanho)}
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Faturamento anual</label>
                  {inputEditable(faturamento, setFaturamento)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Grupos de Empresas */}
        <GrupoEmpresaSection />

        {/* Toast */}
        {toast && (
          <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-bold flex items-center space-x-2 z-50 transition-all ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={toast.type === 'success' ? 'ri-check-line text-sm' : 'ri-error-warning-line text-sm'}></i>
            </div>
            <span>{toast.message}</span>
          </div>
        )}
      </div>
    </Layout>
  );
}